import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, Bell, Download, Clock, CheckCircle, XCircle, 
  AlertCircle, ChevronRight, User, Calendar, Euro
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { inscriptionsAPI, communicationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import Layout from '../layout/Layout';
import ParentOnboarding from '../onboarding/ParentOnboarding';
import PendingAccountModal from '../auth/PendingAccountModal';

const EspaceApprenantPage = () => {
  const { user } = useAuth();
  const [inscriptions, setInscriptions] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [inscRes, commRes] = await Promise.all([
        inscriptionsAPI.list(),
        communicationsAPI.list(true)
      ]);
      setInscriptions(inscRes.data);
      setCommunications(commRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'En attente' },
      validated: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle, label: 'Validée' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Payée' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Annulée' },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#285BBB]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ParentOnboarding />
      <PendingAccountModal />
      <div className="min-h-screen bg-[#F5F7FF] py-8" data-testid="espace-apprenant-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#2C3C65]">Bonjour, {user?.prenom} !</h1>
            <p className="text-[#6777A1] mt-2">Bienvenue dans votre espace apprenant</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-[#2C3C65] mb-4">Actions rapides</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link
                    to="/inscription"
                    className="flex items-center p-4 bg-[#285BBB] text-white rounded-lg hover:bg-[#2C3C65] transition-colors"
                    data-testid="new-inscription-btn"
                  >
                    <FileText className="h-6 w-6 mr-3" />
                    <div>
                      <p className="font-semibold">Nouvelle inscription</p>
                      <p className="text-sm text-blue-200">Inscrire un enfant</p>
                    </div>
                    <ChevronRight className="h-5 w-5 ml-auto" />
                  </Link>
                  <Link
                    to="/contact"
                    className="flex items-center p-4 bg-gray-100 text-[#2C3C65] rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <User className="h-6 w-6 mr-3" />
                    <div>
                      <p className="font-semibold">Nous contacter</p>
                      <p className="text-sm text-[#6777A1]">Poser une question</p>
                    </div>
                    <ChevronRight className="h-5 w-5 ml-auto" />
                  </Link>
                </div>
              </div>

              {/* Inscriptions */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-[#2C3C65]">Mes inscriptions</h2>
                </div>

                {inscriptions.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#6777A1]">Aucune inscription pour le moment</p>
                    <Link
                      to="/inscription"
                      className="inline-flex items-center mt-4 text-[#285BBB] font-medium hover:underline"
                    >
                      Faire une inscription
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {inscriptions.map((inscription) => (
                      <motion.div
                        key={inscription.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-[#2C3C65]">{inscription.numero}</span>
                              {getStatusBadge(inscription.status)}
                            </div>
                            <div className="mt-2 space-y-1 text-sm text-[#6777A1]">
                              <p className="flex items-center">
                                <User className="h-4 w-4 mr-2" />
                                {inscription.enfants?.map(e => `${e.prenom} ${e.nom}`).join(', ')}
                              </p>
                              <p className="flex items-center">
                                <Calendar className="h-4 w-4 mr-2" />
                                {new Date(inscription.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              <p className="flex items-center">
                                <Euro className="h-4 w-4 mr-2" />
                                {inscription.total}€
                              </p>
                            </div>
                          </div>
                          <a
                            href={inscriptionsAPI.getPDF(inscription.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-[#285BBB] hover:text-[#2C3C65]"
                            data-testid={`download-pdf-${inscription.id}`}
                          >
                            <Download className="h-5 w-5 mr-1" />
                            PDF
                          </a>
                        </div>
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-[#6777A1]">
                            <strong>Cours :</strong> {inscription.cours_choisis?.map(c => c.groupe).join(', ')}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Profile Card */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-[#285BBB] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {user?.prenom?.[0]}{user?.nom?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C3C65]">{user?.prenom} {user?.nom}</p>
                    <p className="text-sm text-[#6777A1]">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Communications */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#2C3C65]">Informations</h2>
                  <Bell className="h-5 w-5 text-[#285BBB]" />
                </div>

                {communications.length === 0 ? (
                  <p className="text-sm text-[#6777A1] text-center py-4">Aucune information pour le moment</p>
                ) : (
                  <div className="space-y-3">
                    {communications.slice(0, 5).map((comm) => (
                      <div
                        key={comm.id}
                        className={`p-3 rounded-lg ${
                          comm.type === 'urgent' ? 'bg-red-50 border-l-4 border-red-500' :
                          comm.type === 'event' ? 'bg-blue-50 border-l-4 border-blue-500' :
                          'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start">
                          {comm.type === 'urgent' && <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />}
                          <div>
                            <p className="font-medium text-sm text-[#2C3C65]">{comm.title}</p>
                            <p className="text-xs text-[#6777A1] mt-1 line-clamp-2">{comm.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-xl shadow-md p-6 text-white">
                <h3 className="font-semibold mb-3">Besoin d'aide ?</h3>
                <p className="text-sm text-blue-200 mb-4">Notre équipe est à votre disposition</p>
                <a
                  href="tel:+33626716022"
                  className="block text-sm text-white hover:underline font-medium"
                >
                  06 26 71 60 22
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EspaceApprenantPage;
